import { Form, Formik, FormikProps } from 'formik';
import React, { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PrimaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { Checkbox } from 'src/components/shared/Checkbox/Checkbox';
import { InputField } from 'src/components/shared/Forms/Input/InputField';
import { ModalFooter } from 'src/components/shared/Modal/Modal';
import { InstrumentGroupDetailDto } from 'src/store/instrumentgroups/instrumentgroup';
import {
  maxLength,
  required,
  runValidations,
  validationRunner
} from 'src/utils/validation';

type Props = {
  instrumentGroup: Nullable<InstrumentGroupDetailDto>;
  onCancel: () => void;
  onSubmit: (instrumentGroup: InstrumentGroupDetailDto) => void;
};

export const InstrumentGroupForm = forwardRef<
  FormikProps<InstrumentGroupDetailDto>,
  Props
>((props, ref) => {
  const { onSubmit, onCancel } = props;

  const [instrumentGroup] = useState<InstrumentGroupDetailDto>(
    props.instrumentGroup ||
      ({
        corrected: false
      } as InstrumentGroupDetailDto)
  );

  const { t } = useTranslation();

  const validate = (instrumentGroup: InstrumentGroupDetailDto) => {
    const validationRunners = [
      validationRunner('name', t('props.name'), required, maxLength(50)),
      validationRunner('description', t('props.description'), maxLength(200))
    ];
    return runValidations(instrumentGroup, validationRunners);
  };

  return (
    <Formik
      initialValues={instrumentGroup}
      validate={validate}
      innerRef={ref}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values);
        setSubmitting(false);
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        submitCount
      }) => (
        <Form onSubmit={handleSubmit}>
          <InputField
            name="name"
            label={t('props.name')}
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            errorMessage={errors.name}
            displayError={touched.name || submitCount > 0}
          />
          <InputField
            name="description"
            label={t('props.description')}
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            errorMessage={errors.description}
            displayError={touched.description || submitCount > 0}
          />
          <Checkbox
            label={t('instrumentGroup.corrected')}
            checked={values.corrected}
            name="corrected"
            value={values.corrected}
            onChange={handleChange}
          />

          <ModalFooter>
            <TertiaryButton
              onClick={onCancel}
              label={t('cancel')}
              className="mr-32"
            />
            <PrimaryButton
              type="submit"
              label={t('save.verb')}
              disabled={isSubmitting}
            />
          </ModalFooter>
        </Form>
      )}
    </Formik>
  );
});
